// helix-exception: Helix has no semantic token for these hues (Figma needs-input orange, highlight yellow/amber, categorical teal/cyan); primitive ramps with the identical colour are used (F-11)
// helix-exception: inline text highlight marks keep their 1.15px/3px mark radii; no Helix radius token fits mark geometry (F-12)
import { type HPOSuggestion } from '@/components/common/HPOSearch'
import { TextareaInput, Toggle } from '@/components/common/Modal'
import type { PhenotypeModel } from '@/config/uiConfig'
import { hpoAPIClient, HpoExtractionError, LAB_HPO_VERSION } from '@/features/terminology/hpo/HPOAPIClient'
import { resolveHpoLabel } from '@/features/terminology/hpo/hpoLabelResolver'
import { mapWithConcurrency } from '@/features/terminology/hpo/hpoLookupCache'
import { isHpoCode } from '@/features/terminology/hpo/hpoTypes'
import { clinicalSummaryFingerprint, type ClinicalSummaryReview } from '@/pages/order/clinicalSummaryReviewTypes'
import {
  describesRelative,
  isReplaceMost,
  mergeLiveExtraction,
  reviewForUnreviewedDiagnosis,
  type AnnotationReason,
  type ReplaceSummaryPromptPayload,
} from '@/pages/order/livePhenotypeMerge'
import { useLiveHpoExtraction } from '@/pages/order/useLiveHpoExtraction'
import type { CategorizedHPOOption } from '@/types/RequisitionFormTypes'
import {
  combinedClinicalDescriptionLength,
  SHARED_CLINICAL_TEXT_HELPER,
  SHARED_CLINICAL_TEXT_TOO_LONG_MESSAGE,
  type ClinicalTextBudget,
} from '@/utils/freeTextValidation'
import { CaretRight, Close, User } from 'flowbite-react-icons/outline'
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type Ref,
  type SyntheticEvent,
} from 'react'
import { ClinicalSummaryAddPhenotypePopover } from './ClinicalSummaryAddPhenotypePopover'
import { orphanChipClass } from './clinicalSummaryCategoryStyles'
import { ClinicalSummaryChipMenu, type ClinicalSummaryChipMenuFocus } from './ClinicalSummaryChipMenu'
import { chipMoveTargets } from './clinicalSummaryChipMoveTargets'
import { orderNeedsInputRows, orderSectionChips } from './clinicalSummaryChipOrder'
import { ClinicalSummaryDecisionToast, type ClinicalSummaryToastAction } from './ClinicalSummaryDecisionToast'
import {
  clinicalSummaryDecisionToastMessage,
  clinicalSummaryMoveToastMessage,
  clinicalSummaryOrphanRemovedToastMessage,
  clinicalSummaryReopenCounts,
  clinicalSummaryReopenToastMessage,
  clinicalSummaryTrimToastMessage,
} from './clinicalSummaryDecisionToastMessage'
import { HighlightedSummaryBackdrop, type LinkedSummaryMark } from './clinicalSummaryEditorHighlights'
import {
  buildSummaryHighlightSpans,
  citationSentence,
  citationSnippet,
  extendEditTrail,
  remapSpansAlongTrail,
  type SummaryHighlightSpan,
} from './clinicalSummaryEditorHighlightSpans'
import { ClinicalSummaryHoverTooltipPortal } from './clinicalSummaryEditorTooltips'
import { ClinicalSummaryGeneratingShimmer } from './ClinicalSummaryGeneratingShimmer'
import { ClinicalSummaryGeneratingStatus } from './ClinicalSummaryGeneratingStatus'
import {
  buildGenerationReveal,
  prefersReducedMotion,
  REVEAL_ROW_FIRST_DELAY_MS,
  REVEAL_ROW_STAGGER_MS,
  type GenerationReveal,
} from './clinicalSummaryGenerationReveal'
import { ClinicalSummaryInfoBanner } from './ClinicalSummaryInfoBanner'
import { ClinicalSummaryOrphanNote } from './ClinicalSummaryOrphanNote'
import { ClinicalSummaryPhenotypeBadge } from './ClinicalSummaryPhenotypeBadge'
import { CLINICAL_SUMMARY_PHENOTYPE_MODELS, type ClinicalSummaryPhenotypeOption } from './clinicalSummaryPhenotypeModel'
import { PILL_SHELL_CLASS, WARNING_PILL_TONE_CLASS } from './clinicalSummaryPillStyles'
import {
  citesRelativeFinding,
  clinicalSummaryReasonLabel,
  resolveClinicalSummaryReason,
  type ClinicalSummaryReasonKind,
} from './clinicalSummaryReasonLabels'
import { ClinicalSummaryReplacePrompt } from './ClinicalSummaryReplacePrompt'
import { phenotypeReviewRows } from './clinicalSummaryReviewRows'
import { textareaOffsetFromPoint } from './clinicalSummaryTextareaOffset'
import {
  buildClinicalSummaryTooltipModelForTerm,
  CLINICAL_SUMMARY_TOOLTIP_LAYOUT,
  quotedCitation,
  type ClinicalSummaryHoverTooltipVariant,
  type TooltipAnchorRect,
} from './clinicalSummaryTooltipModel'

const SUMMARY_PLACEHOLDER = 'Type or paste the clinical summary. Phenotypes appear as you write.'
/** A chip the replace prompt would clear: one lavender palette whatever its category. */
const WILL_CLEAR_CHIP_CLASS = 'border-border-brand-subtle dark:border-brand-800 bg-background-brand-softer dark:bg-gray-800 text-brand-950 dark:text-brand-400 opacity-45'
const SECTION_HEADING_CLASS = 'text-lg font-semibold leading-5 tracking-[-0.4px] text-text-heading dark:text-white'
/** Brand outline xs: Figma's stroke is inside the 28px box, so it is an inset ring over the 6/10 padding. */
const HEADER_BUTTON_CLASS =
  'inline-flex h-7 items-center rounded-full border-0 border-border-brand dark:border-brand-500 px-2.5 py-1.5 text-xs font-medium leading-4 text-text-fg-brand dark:text-brand-400 shadow-xs inset-ring inset-ring-border-brand disabled:opacity-50'
const TOMBSTONE_LOOKUP_SPACING_MS = 6500
const TOMBSTONE_LOOKUP_PAUSE_MS = 60_000
const CODE_ONLY_LABEL_RETRY_MS = 60_000
/** How long a decided row stays settled, then collapses (height, opacity and margin to 0). */
const SETTLE_MS = 700
const SETTLE_EXIT_MS = 320
/** How long "Show in summary" keeps its phrase ringed. */
const SHOWN_PHRASE_RING_MS = 1500
/**
 * A chip the clinician has just decided pops .85 → 1.06 → 1 and sheds a ring over the same beat.
 * The ring belongs to the category, so the chip hands the keyframe its own colour: the brand at
 * 40% (brand-600 is #9810fa) for a present phenotype, red for a pertinent negative. Both are
 * written out rather than interpolated, so the utilities stay literal for the CSS build to find.
 */
const DECIDED_CHIP_POP_CLASS = ' rounded-full motion-safe:animate-[clinical-summary-chip-pop_0.7s_ease-out]'
const DECIDED_CHIP_RING_BRAND = ' [--clinical-summary-chip-ring:rgb(var(--color-brand-600-rgb)_/_0.4)]'
const DECIDED_CHIP_RING_NEGATIVE = ' [--clinical-summary-chip-ring:var(--color-border-error-subtle)]'
const decidedChipEnterClass = (category: CategorizedHPOOption['category']) =>
  DECIDED_CHIP_POP_CLASS + (category === 'Excluded' ? DECIDED_CHIP_RING_NEGATIVE : DECIDED_CHIP_RING_BRAND)

interface NeedsInputRowView {
  snippet: ReturnType<typeof citationSnippet>
  reasonKind: ClinicalSummaryReasonKind | null
  citesRelative: boolean
}

/** A row the clinician just decided, kept in its slot as it was drawn then. */
interface SettlingRow {
  key: number
  term: CategorizedHPOOption
  view: NeedsInputRowView
  category: CategorizedHPOOption['category']
  index: number
  leaving: boolean
}

export interface ClinicalSummarySnapshot {
  summary: string
  clinicalDiagnosis: CategorizedHPOOption[]
  review: ClinicalSummaryReview | null | undefined
}

export interface ClinicalSummaryEditorControls {
  /**
   * Call just before unmounting the editor to keep the edit (Save & Close): starts the extraction
   * the summary still owes and lets it, or the one in flight, apply after the editor is gone.
   */
  keepPendingExtraction: () => void
}

interface ClinicalSummaryEditorProps {
  controlRef?: Ref<ClinicalSummaryEditorControls>
  summary: string
  differentialDiagnosis?: string
  priorGeneticTesting?: string
  /** Family history text — counted together with the summary against the order's budget. */
  familyHistory?: string
  /**
   * Characters this summary may use, and how many the rest of the order already
   * uses. Rady caps each description on its own; every other lab shares one
   * order-wide pool, so the counter has to know about comparator summaries too.
   * Omit to hide the counter entirely.
   */
  clinicalTextBudget?: ClinicalTextBudget
  /** Which lab's frames the editor follows where the two designs differ. */
  phenotypeModel?: PhenotypeModel
  /**
   * The order has already been sent, so its phenotypes were decided in an
   * earlier session and persisted.
   *
   * Opts this editor into one background extraction on open, whose results are
   * used to recover removals: a term the extraction finds in the saved summary
   * but which the order does not carry was taken off by the clinician, since
   * anything they accepted would have been saved under a category. Off for the
   * create flow, where an extracted-but-absent term is simply new.
   */
  isSentOrder?: boolean
  /**
   * Opaque docMgmt `reference.id` for this order's clinical-note section. When
   * set (and the feature is on), the attachments uploader renders above the
   * field and an attachment's decoded text is appended to the summary.
   */
  attachmentsReferenceId?: string | null
  clinicalDiagnosis: CategorizedHPOOption[]
  review?: ClinicalSummaryReview | null
  buttonOnly?: boolean
  onSummaryChange: (value: string) => void
  onSupplementalChange: (fields: { differentialDiagnosis: string; priorGeneticTesting: string }) => void
  onReviewChange: (clinicalDiagnosis: CategorizedHPOOption[], review: ClinicalSummaryReview) => void
  onUndoRestore: (snapshot: ClinicalSummarySnapshot) => Promise<void>
}

/**
 * Loaded on demand: the uploader pulls in the shared `@helix-ds/patterns`
 * panel and a DOCX reader, and the feature is off by default — so it must not
 * sit in the bundle every order pays for.
 */
const ClinicalNoteAttachments = lazy(() =>
  import('@/features/documents/ClinicalNoteAttachments').then((m) => ({
    default: m.ClinicalNoteAttachments,
  }))
)

type Status = 'idle' | 'pending' | 'generating' | 'applied' | 'failed'

interface NeedsInputReasons {
  extractionReasons: Record<string, string>
  annotationReasons: Record<string, AnnotationReason | null>
  contradictionCodes: ReadonlySet<string>
}

/** An extraction's Undo: the order before it ran, and the reasons its needs-input rows were asked with then. */
type ExtractionUndo = ClinicalSummarySnapshot & { reasons: NeedsInputReasons }

/** What a toast's Undo puts back. */
type ToastUndo =
  | {
      kind: 'decision'
      clinicalDiagnosis: CategorizedHPOOption[]
      review: ClinicalSummaryReview | null | undefined
      /** The text when the decision was made: undoing it must not vouch for edits made since. */
      summary: string
      extractionUndo: ExtractionUndo | null
    }
  /** The last extraction's snapshot: the status-row Undo, moved onto the toast. */
  | { kind: 'extraction' }

interface EditorToast {
  message: string
  undo?: ToastUndo
  takeFocus?: boolean
  durationMs?: number
}

/** Long enough to read a sentence-length toast and reach its Undo. */
const LONG_TOAST_MS = 8000

interface HoverTooltip {
  code: string
  variant: ClinicalSummaryHoverTooltipVariant
  anchor: TooltipAnchorRect
  /** Keyboard focus opened it (caret in a highlight, focused chip); otherwise the pointer did. */
  source: 'pointer' | 'focus'
  /** The highlighted span it describes, so moving within that span does not move it. */
  begin?: number
  /** Keyboard-opened: the chip or mark it is re-anchored to, rather than closed, when something scrolls. */
  anchorElement?: HTMLElement
}

function categoryFromSuggestionType(type: HPOSuggestion['type'] | undefined): CategorizedHPOOption['category'] {
  if (type === 'additional') return 'Additional'
  return 'Key'
}

function pickCodes<T>(values: Record<string, T>, keep: (code: string) => boolean): Record<string, T> {
  return Object.fromEntries(Object.entries(values).filter(([code]) => keep(code)))
}

/** The element's first line box: the whole box for a chip, the first line of a wrapped mark. */
function anchorRect(element: HTMLElement): TooltipAnchorRect {
  const { top, bottom, left } = element.getClientRects()[0] ?? element.getBoundingClientRect()
  return { top, bottom, left }
}

/** A label worth showing and storing: the HP id is the fallback, not a name. */
function displayLabel(term: CategorizedHPOOption | undefined): string | undefined {
  if (term?.label && !isHpoCode(term.label)) return term.label
  if (term?.node?.lbl && !isHpoCode(term.node.lbl)) return term.node.lbl
  return undefined
}

export function ClinicalSummaryEditor({
  controlRef,
  summary,
  differentialDiagnosis = '',
  priorGeneticTesting = '',
  familyHistory = '',
  clinicalTextBudget,
  phenotypeModel = 'rady',
  isSentOrder = false,
  attachmentsReferenceId,
  clinicalDiagnosis,
  review,
  buttonOnly = false,
  onSummaryChange,
  onSupplementalChange,
  onReviewChange,
  onUndoRestore,
}: ClinicalSummaryEditorProps) {
  const model = CLINICAL_SUMMARY_PHENOTYPE_MODELS[phenotypeModel]
  const trimToast = model.toasts.trim
  const [status, setStatus] = useState<Status>('idle')
  const [error, setError] = useState<string | null>(null)
  const [searchOpen, setSearchOpen] = useState(false)
  const addButtonRef = useRef<HTMLButtonElement>(null)
  const [acceptedText, setAcceptedText] = useState(summary)
  const [undo, setUndo] = useState<ExtractionUndo | null>(null)
  const [undoPending, setUndoPending] = useState(false)
  const hasDifferentialText = Boolean(differentialDiagnosis.trim() || priorGeneticTesting.trim())
  const [differentialOpen, setDifferentialOpen] = useState(hasDifferentialText)
  /** The saved order can load after mount; open for its text once, then leave the toggle to the clinician. */
  const differentialOpenedForText = useRef(hasDifferentialText)
  const [selectedPillId, setSelectedPillId] = useState<string | null>(null)
  const [chipMenuFocus, setChipMenuFocus] = useState<ClinicalSummaryChipMenuFocus | null>(null)
  const chipIdBase = useId()
  const chipTriggerId = (code: string) => `${chipIdBase}-chip-${code}`
  /** A chip to focus once the next render has put it where it now lives. */
  const chipToFocus = useRef<{ code: string; preventScroll: boolean } | null>(null)
  const [settlingRows, setSettlingRows] = useState<SettlingRow[]>([])
  const settlingKey = useRef(0)
  /** Only the timers still pending, so a long session of decisions cannot accumulate fired ids. */
  const settleTimers = useRef(new Set<ReturnType<typeof setTimeout>>())
  const [shownPhrase, setShownPhrase] = useState<LinkedSummaryMark | null>(null)
  const [hoverTooltip, setHoverTooltip] = useState<HoverTooltip | null>(null)
  const hoverTooltipId = useId()
  const [toast, setToast] = useState<EditorToast | null>(null)
  const setDecisionToast = useCallback((message: string | null) => setToast(message ? { message } : null), [])
  /** Polite announcements with no toast of their own: the replace prompt opening and its actions. */
  const [announcement, setAnnouncement] = useState('')
  const editorRef = useRef<HTMLElement>(null)
  const savedReviewChecked = useRef(false)
  /** The review has changed in this editor, so a review arriving now is not the saved order's. */
  const reviewChangedHere = useRef(false)
  const [replacePrompt, setReplacePrompt] = useState<ReplaceSummaryPromptPayload | null>(null)
  /** The order as it was before the replacement, for "Undo the replacement". */
  const [replaceBaseline, setReplaceBaseline] = useState<ClinicalSummarySnapshot | null>(null)
  /** A saved order opens with its removals in view; a group started in this session opens on request. */
  const [removedOpen, setRemovedOpen] = useState(() => (review?.tombstones.length ?? 0) > 0)
  /** Labels for tombstoned codes (diagnosis rows are dropped on remove). */
  const [removedLabels, setRemovedLabels] = useState<Record<string, string>>({})
  const [extractionReasons, setExtractionReasons] = useState<Record<string, string>>({})
  const [annotationReasons, setAnnotationReasons] = useState<Record<string, AnnotationReason | null>>({})
  /** Codes the last extraction found contradicting the clinician's recorded presence/absence. */
  const [contradictionCodes, setContradictionCodes] = useState<ReadonlySet<string>>(() => new Set())
  /** Bumped per generation so the status line's timers restart with it. */
  const [generationRun, setGenerationRun] = useState(0)
  const [reveal, setReveal] = useState<GenerationReveal | null>(null)
  const latestPhenotypeTotal = useRef(0)
  const requestSequence = useRef(0)
  const attemptedTombstoneLabelIds = useRef(new Set<string>())
  const activeRequest = useRef<AbortController | null>(null)
  const pastePending = useRef(false)
  const lastEmittedSummary = useRef(summary)
  const latestSummary = useRef(summary)
  const latestDiagnosis = useRef(clinicalDiagnosis)
  const latestReview = useRef(review)
  /**
   * Set only for the extraction fired when a saved order's summary is opened, so
   * that run may read an extracted-but-absent term as a removal. Cleared
   * immediately after, because every later run is the clinician editing.
   */
  const inferRemovalsOnThisRun = useRef(false)
  const latestAcceptedText = useRef(acceptedText)
  const latestReplaceBaseline = useRef(replaceBaseline)
  const latestReplacePrompt = useRef(replacePrompt)
  const latestReasons = useRef<NeedsInputReasons>({ extractionReasons, annotationReasons, contradictionCodes })
  latestReasons.current = { extractionReasons, annotationReasons, contradictionCodes }
  /** The edits from `acceptedText` to the summary on screen; assigned where the render builds it. */
  const latestEditTrail = useRef<readonly string[]>([acceptedText])
  /** Earliest time for the next tombstone label lookup: paced, and paused longer after a failure. */
  const nextTombstoneLookupAt = useRef(0)
  /** Bumped when a failure's pause ends, so the remaining tombstones are looked up. */
  const [tombstoneLookupResumes, setTombstoneLookupResumes] = useState(0)
  const tombstoneResumeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const summaryBackdropRef = useRef<HTMLDivElement>(null)
  const summaryTextareaRef = useRef<HTMLTextAreaElement>(null)
  latestSummary.current = summary
  latestDiagnosis.current = clinicalDiagnosis
  latestReview.current = review
  latestAcceptedText.current = acceptedText
  latestReplaceBaseline.current = replaceBaseline
  latestReplacePrompt.current = replacePrompt
  /** Names for codes no longer in the diagnosis: stored on the review at removal, else resolved this session. */
  const knownLabels = useMemo(() => {
    const labels: Record<string, string> = {}
    for (const [code, termReview] of Object.entries(review?.terms ?? {})) {
      if (termReview.label && !isHpoCode(termReview.label)) labels[code] = termReview.label
    }
    return { ...labels, ...removedLabels }
  }, [review, removedLabels])
  const latestKnownLabels = useRef(knownLabels)
  latestKnownLabels.current = knownLabels

  const dismissHoverTooltip = useCallback(() => setHoverTooltip(null), [])
  const dismissDecisionToast = useCallback(() => setToast(null), [])
  const focusSummaryField = () => summaryTextareaRef.current?.focus({ preventScroll: true })
  /** The field is disabled while an undo is pending, so focus waits for the render that enables it. */
  const focusFieldWhenEnabled = useRef(false)
  useEffect(() => {
    if (!focusFieldWhenEnabled.current || undoPending) return
    focusFieldWhenEnabled.current = false
    summaryTextareaRef.current?.focus({ preventScroll: true })
  })
  /** Focus left a closing toast: the first open row, else the summary field. */
  const returnFocusFromToast = useCallback(() => {
    const nextRowControl = editorRef.current?.querySelector<HTMLButtonElement>(
      '[role="group"][aria-label^="Set category for "] button:not(:disabled)'
    )
    ;(nextRowControl ?? summaryTextareaRef.current)?.focus({ preventScroll: true })
  }, [])
  /** Codes whose label is resolved or being resolved; one whose lookup failed waits for the next retry. */
  const attemptedCodeOnlyLabelIds = useRef(new Set<string>())
  /** Bumped when the pause after a failed label lookup ends, so the failed codes are looked up again. */
  const [codeOnlyLabelRetries, setCodeOnlyLabelRetries] = useState(0)
  const codeOnlyLabelRetryTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const unmounted = useRef(false)
  useEffect(() => {
    unmounted.current = false
    return () => {
      unmounted.current = true
      if (codeOnlyLabelRetryTimer.current) clearTimeout(codeOnlyLabelRetryTimer.current)
      codeOnlyLabelRetryTimer.current = null
    }
  }, [])

  useEffect(() => {
    if (!hasDifferentialText || differentialOpenedForText.current) return
    differentialOpenedForText.current = true
    setDifferentialOpen(true)
  }, [hasDifferentialText])

  // The saved order can arrive after mount; only a review this editor has not changed is the saved one.
  // Informational only: undo does not reach back past the session that made the change.
  // Layout effect: a saved order's Removed group must not paint collapsed for a frame before it opens.
  useLayoutEffect(() => {
    if (savedReviewChecked.current || !review) return
    savedReviewChecked.current = true
    if (reviewChangedHere.current) return
    if (review.tombstones.length > 0) setRemovedOpen(true)
    if (!model.toasts.reopen) return
    const message = clinicalSummaryReopenToastMessage(
      clinicalSummaryReopenCounts(review, new Set(clinicalDiagnosis.map((term) => term.id)))
    )
    if (message) setToast({ message, durationMs: LONG_TOAST_MS })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [review])

  // Session hydration (and conflict reload) can replace `summary` after mount.
  // Treat those external updates as already-accepted so blur/typing does not
  // re-extract over persisted phenotype review metadata.
  useEffect(() => {
    if (summary === lastEmittedSummary.current) return
    lastEmittedSummary.current = summary
    setAcceptedText(summary)
  }, [summary])

  // Legacy / Keep paths can leave `label` as an HP code (especially Excluded).
  // Resolve pending codes so chips and Needs-your-input rows show the human name.
  useEffect(() => {
    if (clinicalDiagnosis.length === 0) return
    const pending = clinicalDiagnosis.filter(
      (term) =>
        isHpoCode(term.label) &&
        (!term.node?.lbl || isHpoCode(term.node.lbl)) &&
        !attemptedCodeOnlyLabelIds.current.has(term.id)
    )
    if (pending.length === 0) return

    for (const term of pending) {
      attemptedCodeOnlyLabelIds.current.add(term.id)
    }

    void (async () => {
      const getTermById = (id: string) => hpoAPIClient.getHPOTermById(id)
      const labelById = new Map<string, string>()
      let failed = false
      await mapWithConcurrency(pending, 2, async (term) => {
        try {
          labelById.set(term.id, (await resolveHpoLabel(term, getTermById)).label)
        } catch {
          failed = true
          attemptedCodeOnlyLabelIds.current.delete(term.id)
        }
      })
      if (unmounted.current) return
      if (failed && !codeOnlyLabelRetryTimer.current) {
        codeOnlyLabelRetryTimer.current = setTimeout(() => {
          codeOnlyLabelRetryTimer.current = null
          setCodeOnlyLabelRetries((retries) => retries + 1)
        }, CODE_ONLY_LABEL_RETRY_MS)
      }
      // The order may have changed while the names loaded: name what is on it now, keep its review as it is now.
      // Only the name changes: the review, its citations and the category are keyed by the term's id.
      let named = false
      const updated = latestDiagnosis.current.map((term) => {
        const label = labelById.get(term.id)
        if (!label || !isHpoCode(term.label) || label === term.label) return term
        named = true
        return {
          ...term,
          label,
          node: {
            ...term.node,
            id: term.node?.id ?? term.id,
            lbl: label,
            type: term.node?.type ?? 'PhenotypicFeature',
          },
        }
      })
      if (!named) return
      // This write is the editor's own: the saved-order toast must not read it as loaded decisions.
      reviewChangedHere.current = true
      onReviewChange(updated, latestReview.current ?? reviewForUnreviewedDiagnosis(updated, latestSummary.current))
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicalDiagnosis, codeOnlyLabelRetries])

  // Text a lab does not collect is not the clinician's to trim, so it never counts against a limit.
  const combinedLength =
    combinedClinicalDescriptionLength(
      summary,
      familyHistory,
      model.collectsDifferential ? differentialDiagnosis : '',
      model.collectsDifferential ? priorGeneticTesting : ''
    ) + (clinicalTextBudget?.usedElsewhere ?? 0)
  const overCombinedLimit = clinicalTextBudget ? combinedLength > clinicalTextBudget.limit : false
  const savedSupplemental = [
    { label: 'Differential Diagnoses', value: differentialDiagnosis.trim() },
    { label: 'Prior Genetic Testing', value: priorGeneticTesting.trim() },
  ].filter(({ value }) => value)
  const combinedCounterProps = clinicalTextBudget
    ? {
        characterCount: combinedLength,
        characterLimit: clinicalTextBudget.limit,
        characterCountOverLimit: overCombinedLimit,
      }
    : {}

  const extract = useCallback(
    async (capturedText: string) => {
      const sequence = ++requestSequence.current
      /**
       * Consumed here, not after the merge.
       *
       * This run owns the flag, so it must be cleared before the first `await`:
       * an aborted or superseded request returns early, and a flag left set
       * would leak into the clinician's next extraction — suppressing its
       * shimmer and, far worse, making it infer removals, silently tombstoning
       * phrases they had just typed.
       */
      const silent = inferRemovalsOnThisRun.current
      inferRemovalsOnThisRun.current = false
      activeRequest.current?.abort()
      const controller = new AbortController()
      activeRequest.current = controller
      // The open-time run is background work: the clinician asked for nothing and
      // the summary already has marks from the persisted review. Entering
      // `generating` would blank the highlights and start the shimmer, so an
      // order that rendered instantly would look like it was still loading.
      if (!silent) {
        setStatus('generating')
        setGenerationRun((run) => run + 1)
        setReveal(null)
      }
      setError(null)
      try {
        // A cleared note cites nothing, so its terms leave the summary like any others the text drops.
        const extraction = capturedText.trim()
          ? (
              await hpoAPIClient.construeHPO(
                capturedText,
                { hpoVersion: LAB_HPO_VERSION, annotate: true },
                controller.signal
              )
            ).filter((code) => !describesRelative(code))
          : []
        // A newer request owns `generating`; leave status alone.
        if (sequence !== requestSequence.current) return
        // Summary changed while in flight — do not apply, but clear `generating`
        // so Generate is not stuck disabled until the next successful run.
        if (capturedText !== latestSummary.current) {
          setStatus('pending')
          return
        }
        // While the prompt is open, later edits are still judged against the text before the replacement.
        const baseline = latestReplaceBaseline.current ?? {
          summary: latestAcceptedText.current,
          clinicalDiagnosis: latestDiagnosis.current,
          review: latestReview.current,
        }
        const replaceMode = isReplaceMost(baseline.summary, capturedText)
        const merged = mergeLiveExtraction({
          summary: capturedText,
          clinicalDiagnosis: latestDiagnosis.current,
          review: latestReview.current,
          extraction,
          replaceMode,
          // Only the one extraction fired on opening a saved order may read an
          // extracted-but-absent term as a removal. Every later run is the
          // clinician editing, where a new phrase is a genuine addition.
          inferRemovalsFromExtraction: silent,
        })
        const reasons: Record<string, string> = {}
        for (const code of extraction) {
          const key = code.code?.toUpperCase?.() ?? code.code
          const reasonText = (code.reason ?? code.rationale ?? '').trim()
          if (key && reasonText) reasons[key] = reasonText
        }
        const promptOpen = replaceMode && merged.priorOnly.length > 0
        // Later edits re-run the merge while the prompt is open, when the folded questions already read as active.
        const questionCodes = promptOpen
          ? [
              ...new Set([
                ...(latestReplacePrompt.current?.questionCodes ?? []).filter((code) => merged.priorOnly.includes(code)),
                ...merged.priorOnlyQuestions,
              ]),
            ]
          : []
        // A question the new text no longer returns keeps the reason it was asked with.
        const extractedCodes = new Set(extraction.map((code) => code.code?.toUpperCase?.() ?? code.code))
        const stillAsked = (code: string) =>
          !extractedCodes.has(code) &&
          (questionCodes.includes(code) || merged.review.terms[code]?.reviewState === 'needs-input')
        setExtractionReasons((previous) => ({ ...pickCodes(previous, stillAsked), ...reasons }))
        setAnnotationReasons((previous) => ({
          ...pickCodes(previous, stillAsked),
          ...merged.summaryFlags.annotationReasons,
        }))
        setContradictionCodes(new Set(merged.summaryFlags.contradictions))
        if (promptOpen) {
          if (!latestReplacePrompt.current) {
            const count = merged.priorOnly.length
            setAnnouncement(
              `You replaced most of the summary. ${count} phenotype${count === 1 ? '' : 's'} came only from the previous text.`
            )
          }
          setReplaceBaseline(baseline)
          setReplacePrompt({
            orphanCount: merged.priorOnly.length,
            orphanCodes: merged.priorOnly,
            questionCodes,
            keptCount: merged.keptByClinician.length,
          })
        } else {
          setReplacePrompt(null)
          setReplaceBaseline(null)
        }
        setUndo({
          summary: latestAcceptedText.current,
          clinicalDiagnosis: latestDiagnosis.current,
          review: latestReview.current,
          reasons: latestReasons.current,
        })
        setReveal(
          silent || prefersReducedMotion()
            ? null
            : buildGenerationReveal({
                id: sequence,
                summary: capturedText,
                clinicalDiagnosis: merged.clinicalDiagnosis,
                review: merged.review,
                previousReview: latestReview.current,
                hadPhenotypes: latestPhenotypeTotal.current > 0,
              })
        )
        const leftSummary = merged.summaryFlags.notInSummary.filter(
          (code) => latestReview.current?.terms[code]?.reviewState !== 'not-in-summary'
        )
        // An open toast's Undo would put back the review this extraction replaces.
        setToast((current) =>
          trimToast && !promptOpen && leftSummary.length > 0
            ? {
                message: clinicalSummaryTrimToastMessage(leftSummary.length),
                undo: { kind: 'extraction' },
                durationMs: LONG_TOAST_MS,
              }
            : current?.undo
              ? null
              : current
        )
        reviewChangedHere.current = true
        onReviewChange(merged.clinicalDiagnosis, merged.review)
        setAcceptedText(capturedText)
        // `applied` drives the reveal animation and the "phenotypes found" counter,
        // which would announce work the clinician never asked for.
        setStatus(silent ? 'idle' : 'applied')
      } catch (caught) {
        if (sequence !== requestSequence.current) return
        if (caught instanceof DOMException && caught.name === 'AbortError') {
          setStatus('idle')
          return
        }
        // As on success: the text this request was for is gone, and the edit schedules its own.
        if (capturedText !== latestSummary.current) {
          setStatus('pending')
          return
        }
        /**
         * A failed background run stays quiet.
         *
         * The clinician never asked for this extraction and saw no sign it was
         * running, so a "Generation failed" dot and an error banner would be
         * unexplained — and nothing is lost: the summary keeps the marks from
         * its persisted review, and only the recovery of removals is missed.
         * An explicit Generate still reports its failure.
         */
        if (silent) {
          setStatus('idle')
          return
        }
        const statusCode = caught instanceof HpoExtractionError ? caught.status : undefined
        setStatus('failed')
        if (statusCode === 403) {
          setError('Phenotype generation is not permitted for this session. Your existing terms were kept.')
        } else {
          setError('Phenotype generation is temporarily unavailable. Your existing terms were kept.')
        }
        // Align review fingerprint with the note that failed to extract so submit
        // is not stranded on a stale digest (design: extraction failure must leave
        // the draft usable). Keep phenotype rows; carry each citation through the edits
        // the way the marks are, so none quotes words the edit changed.
        const diagnosis = latestDiagnosis.current
        const prior = latestReview.current
        const trail = latestEditTrail.current
        reviewChangedHere.current = true
        if (prior) {
          onReviewChange(diagnosis, {
            ...prior,
            summaryFingerprint: clinicalSummaryFingerprint(capturedText),
            terms: Object.fromEntries(
              Object.entries(prior.terms).map(([code, termReview]) => [
                code,
                {
                  ...termReview,
                  citations: remapSpansAlongTrail(termReview.citations, trail).filter(
                    ({ begin, end }) =>
                      Number.isInteger(begin) &&
                      Number.isInteger(end) &&
                      begin >= 0 &&
                      begin < end &&
                      end <= capturedText.length
                  ),
                },
              ])
            ),
          })
        } else if (diagnosis.length > 0) {
          onReviewChange(diagnosis, reviewForUnreviewedDiagnosis(diagnosis, capturedText))
        }
        setAcceptedText(capturedText)
      } finally {
        // Only while a request is still the active one does aborting it cancel anything.
        if (activeRequest.current === controller) activeRequest.current = null
      }
    },
    [onReviewChange, trimToast]
  )

  const scheduling = useLiveHpoExtraction({
    enabled: true,
    buttonOnly,
    text: summary,
    acceptedText,
    onExtract: extract,
  })

  // Conflict reload remounts this editor via `clinicalSummaryEditorEpoch`; cancel
  // any debounce timer and in-flight construe so a late onReviewChange cannot
  // overwrite the order just reloaded from the server.
  const cancelScheduled = scheduling.cancelScheduled
  const keepExtractionOnUnmount = useRef(false)

  /**
   * Re-extract once when a saved order's summary is opened.
   *
   * Nothing else will: hydration marks an externally-supplied summary as
   * already-accepted (see the `setAcceptedText` effect above) precisely so blur
   * and typing do not re-extract over persisted review metadata. That leaves a
   * reopened order with no term list, and the merge infers a removal from
   * "extraction finds this phrase but the order does not carry the term" — so
   * with no extraction there is nothing to infer from and the clinician's
   * removals read as though they never happened.
   *
   * Runs on the modal opening rather than on page load, so viewing an order
   * costs no extraction: the marks only exist inside this editor anyway. Fires
   * at most once per mount, and only when the summary has text whose terms were
   * decided in an earlier session.
   */
  const generateNow = scheduling.generate
  const openExtractionDone = useRef(false)
  useEffect(() => {
    if (openExtractionDone.current) return
    // Create flow: an extracted-but-absent term is simply new, so there is
    // nothing to recover and no reason to spend an extraction.
    if (!isSentOrder) return
    if (!summary.trim()) return
    // A draft mid-edit is already scheduling its own extraction; only a summary
    // that arrived already-accepted needs this.
    if (summary !== acceptedText) return
    // A review that already records tombstones has nothing to recover, and
    // re-extracting it would re-open questions the clinician has answered.
    //
    // An empty tombstone list is not proof that nothing was removed: the server
    // rebuilds the review from HPO rows, and a term removed before the order was
    // first saved never had a row to tombstone.
    if (review != null && review.tombstones.length > 0) return
    if (clinicalDiagnosis.length === 0) return
    /**
     * Deferred by a tick, and the timer is cancelled on teardown.
     *
     * Firing inline raced with this editor's own unmount cleanup, which aborts
     * any in-flight construe. React's double-invoked effects in development ran
     * it, aborted it, then found the already-fired guard set and never retried —
     * so removals only appeared once the clinician touched the field and blurred,
     * which scheduled an extraction the normal way. The timer makes the guard
     * and the request commit together, after the mount has settled.
     */
    const timer = setTimeout(() => {
      openExtractionDone.current = true
      inferRemovalsOnThisRun.current = true
      generateNow()
    }, 0)
    return () => clearTimeout(timer)
  }, [isSentOrder, summary, acceptedText, review, clinicalDiagnosis, generateNow])

  useEffect(() => {
    return () => {
      cancelScheduled()
      if (keepExtractionOnUnmount.current) return
      requestSequence.current += 1
      activeRequest.current?.abort()
      activeRequest.current = null
    }
  }, [cancelScheduled])

  const flushExtraction = scheduling.blur

  /**
   * The one path text takes into the summary, whether the clinician typed it,
   * pasted it, or it arrived as the decoded text of an attachment. Keeping the
   * uploader on this path means attached text is reviewed like any other edit
   * instead of landing in the order with no phenotypes extracted from it.
   */
  const commitSummaryText = useCallback(
    (value: string, trigger: 'paste' | 'typing') => {
      // Never reviewed (an order from before this editor): stamp the text as it was, so
      // submit waits for the extraction that checks this edit.
      if (!latestReview.current && latestDiagnosis.current.length > 0) {
        reviewChangedHere.current = true
        onReviewChange(
          latestDiagnosis.current,
          reviewForUnreviewedDiagnosis(latestDiagnosis.current, latestSummary.current)
        )
      }
      lastEmittedSummary.current = value
      onSummaryChange(value)
      setUndo(null)
      setReveal(null)
      setError(null)
      setStatus(value === acceptedText ? 'idle' : 'pending')
      scheduling.schedule(trigger, value)
    },
    [acceptedText, onReviewChange, onSummaryChange, scheduling]
  )

  useImperativeHandle(
    controlRef,
    () => ({
      keepPendingExtraction: () => {
        keepExtractionOnUnmount.current = true
        flushExtraction()
      },
    }),
    [flushExtraction]
  )

  const commitReview = (nextDiagnosis: CategorizedHPOOption[], nextReview: ClinicalSummaryReview) => {
    setUndo(null)
    // Any open toast's Undo was taken against the review this replaces.
    setToast(null)
    reviewChangedHere.current = true
    onReviewChange(nextDiagnosis, nextReview)
  }

  // An order from before this editor has no review: manual edits stamp every row as it is, against
  // the current summary, so the untouched rows keep their origin and submit is not blocked.
  // Memoized because the fallback is a fresh object, which would defeat every memo keyed on it.
  const editedReview = useMemo(
    () => review ?? reviewForUnreviewedDiagnosis(clinicalDiagnosis, summary),
    [review, clinicalDiagnosis, summary]
  )
  const lastMergedSummaryFingerprint = editedReview.summaryFingerprint

  const changeCategory = (
    code: string,
    category: CategorizedHPOOption['category'],
    { fromKeyboard = false, moved = false }: { fromKeyboard?: boolean; moved?: boolean } = {}
  ) => {
    const before: ToastUndo = { kind: 'decision', clinicalDiagnosis, review, summary, extractionUndo: undo }
    dismissHoverTooltip()
    setSelectedPillId(null)
    const existing = clinicalDiagnosis.find((term) => term.id === code)
    const label = knownLabels[code] ?? code
    const resolvedTerm: CategorizedHPOOption = existing
      ? { ...existing, category }
      : {
          id: code,
          label,
          category,
          source: 'manual',
          node: { id: code, lbl: label, type: 'PhenotypicFeature' },
        }
    // A chip order that appends placed terms reads when they joined from the diagnosis order.
    const nextDiagnosis =
      existing && model.chipOrder === 'document'
        ? clinicalDiagnosis.map((term) => (term.id === code ? resolvedTerm : term))
        : [...clinicalDiagnosis.filter((term) => term.id !== code), resolvedTerm]
    const current = editedReview.terms[code]
    const nextReview: ClinicalSummaryReview = {
      schemaVersion: 1,
      writerMode: 'merged-review-v1',
      summaryFingerprint: lastMergedSummaryFingerprint,
      terms: {
        ...editedReview.terms,
        [code]: {
          origin: current?.origin ?? 'clinician',
          categoryOwner: 'clinician',
          reviewState: 'active',
          citations: current?.citations ?? [],
        },
      },
      tombstones: editedReview.tombstones.filter((item) => item !== code),
    }
    commitReview(nextDiagnosis, nextReview)
    const message = (moved ? clinicalSummaryMoveToastMessage : clinicalSummaryDecisionToastMessage)(
      resolvedTerm.label,
      model.categoryNames[category]
    )
    // The decided row is gone or settled past focus; from the keyboard, Undo is the next Tab stop.
    setToast({ message, undo: before, takeFocus: fromKeyboard })
  }

  const undoFromToast = async (toastUndo: ToastUndo) => {
    if (toastUndo.kind === 'decision') {
      onReviewChange(
        toastUndo.clinicalDiagnosis,
        toastUndo.review ?? reviewForUnreviewedDiagnosis(toastUndo.clinicalDiagnosis, toastUndo.summary)
      )
      setUndo(toastUndo.extractionUndo)
    } else if (undo) {
      await restoreExtractionSnapshot(undo)
    }
  }

  const removeTerm = (code: string, { fromKeyboard = false }: { fromKeyboard?: boolean } = {}) => {
    // Taken before the commit: `commitReview` drops the open toast, so the Undo has to hold the
    // state this removal replaces, the way a category change does.
    const before: ToastUndo = { kind: 'decision', clinicalDiagnosis, review, summary, extractionUndo: undo }
    dismissHoverTooltip()
    setSelectedPillId(null)
    const label = displayLabel(clinicalDiagnosis.find((term) => term.id === code)) ?? knownLabels[code]
    if (label) setRemovedLabels((prev) => ({ ...prev, [code]: label }))
    const nextDiagnosis = clinicalDiagnosis.filter((term) => term.id !== code)
    const current = editedReview.terms[code]
    commitReview(nextDiagnosis, {
      schemaVersion: 1,
      writerMode: 'merged-review-v1',
      summaryFingerprint: lastMergedSummaryFingerprint,
      terms: {
        ...editedReview.terms,
        [code]: {
          origin: current?.origin ?? 'clinician',
          categoryOwner: current?.categoryOwner ?? 'clinician',
          reviewState: 'removed',
          // The phrase stays marked as considered and removed, and Restore gets its marks back.
          citations: current?.citations ?? [],
          ...(label ? { label } : {}),
        },
      },
      tombstones: [...new Set([...editedReview.tombstones, code])],
    })
    // Removal is reversible: the term sits in the Removed group, and Undo puts it back where it was.
    // From the keyboard the toast takes focus only when no neighbouring chip claimed it first.
    setToast({ message: `${label ?? code} removed from the order`, undo: before, takeFocus: fromKeyboard })
  }

  /** A removed term's category is not stored, so it comes back as Key (SeqOne's Present). */
  const restoreTerm = (code: string, fromKeyboard: boolean) => {
    const current = review?.terms[code]
    if (!review || !current) return
    const label = knownLabels[code] ?? code
    const source = current.origin === 'clinician' ? 'manual' : 'phenoml'
    commitReview(
      [
        ...clinicalDiagnosis,
        { id: code, label, category: 'Key', source, node: { id: code, lbl: label, type: 'PhenotypicFeature' } },
      ],
      {
        ...review,
        summaryFingerprint: lastMergedSummaryFingerprint,
        terms: { ...review.terms, [code]: { ...current, reviewState: 'active' } },
        tombstones: review.tombstones.filter((item) => item !== code),
      }
    )
    // The Restore button leaves with its chip; from the keyboard, focus follows the term to its new chip.
    if (fromKeyboard) chipToFocus.current = { code, preventScroll: false }
  }

  const addTerm = (
    suggestion: HPOSuggestion,
    category: CategorizedHPOOption['category'] = categoryFromSuggestionType(suggestion.type)
  ) => {
    if (clinicalDiagnosis.some((term) => term.id === suggestion.id || term.id === suggestion.hpoCode)) return
    const code = suggestion.hpoCode || suggestion.id
    const term: CategorizedHPOOption = {
      id: code,
      label: suggestion.name,
      category,
      source: 'manual',
      node: { id: code, lbl: suggestion.name, type: 'PhenotypicFeature' },
    }
    commitReview([...clinicalDiagnosis, term], {
      schemaVersion: 1,
      writerMode: 'merged-review-v1',
      summaryFingerprint: lastMergedSummaryFingerprint,
      terms: {
        ...editedReview.terms,
        [code]: { origin: 'clinician', categoryOwner: 'clinician', reviewState: 'active', citations: [] },
      },
      tombstones: editedReview.tombstones.filter((item) => item !== code),
    })
    setDecisionToast(clinicalSummaryDecisionToastMessage(term.label, model.categoryNames[category]))
  }

  /** Takes codes off the order as the × does, keeping their labels for the Removed group. */
  const tombstoneCodes = (codes: readonly string[]) => {
    let nextDiagnosis = clinicalDiagnosis
    let nextReview = review
    const labelsToKeep: Record<string, string> = {}
    for (const code of codes) {
      const label = displayLabel(nextDiagnosis.find((term) => term.id === code)) ?? knownLabels[code]
      if (label) labelsToKeep[code] = label
      nextDiagnosis = nextDiagnosis.filter((term) => term.id !== code)
      const current = nextReview?.terms[code]
      nextReview = {
        schemaVersion: 1,
        writerMode: 'merged-review-v1',
        summaryFingerprint: lastMergedSummaryFingerprint,
        terms: {
          ...(nextReview?.terms ?? {}),
          [code]: {
            origin: current?.origin ?? 'service',
            categoryOwner: current?.categoryOwner ?? 'service',
            reviewState: 'removed',
            citations: current?.citations ?? [],
            ...(label ? { label } : {}),
          },
        },
        tombstones: [...new Set([...(nextReview?.tombstones ?? []), code])],
      }
    }
    if (Object.keys(labelsToKeep).length > 0) {
      setRemovedLabels((prev) => ({ ...prev, ...labelsToKeep }))
    }
    if (nextReview) commitReview(nextDiagnosis, nextReview)
  }

  /**
   * The prompt's answer is the last word on the replacement, so a merge still in flight must not
   * land: it would judge the text against the summary before the replacement all over again and
   * put the phenotypes the clinician just decided on back.
   */
  const dropExtractionInFlight = () => {
    const inFlight = activeRequest.current !== null
    requestSequence.current += 1
    activeRequest.current?.abort()
    activeRequest.current = null
    // An abort cancelled this side leaves the status alone, so say what is true of the text now.
    if (inFlight) setStatus(latestSummary.current === latestAcceptedText.current ? 'idle' : 'pending')
  }

  const clearReplaceOrphans = () => {
    if (!replacePrompt) return
    dismissHoverTooltip()
    dropExtractionInFlight()
    tombstoneCodes(replacePrompt.orphanCodes)
    const cleared = replacePrompt.orphanCodes.length
    setAnnouncement(`Cleared ${cleared} phenotype${cleared === 1 ? '' : 's'} that came only from the previous text.`)
    setReplacePrompt(null)
    setReplaceBaseline(null)
    // The prompt and the button that had focus are about to unmount.
    focusSummaryField()
  }

  const keepReplaceEverything = () => {
    if (replacePrompt && review) {
      const terms = { ...review.terms }
      const questions = new Set(replacePrompt.questionCodes)
      // Kept terms become the clinician's, so no later extraction reports them as "Not in summary".
      // Keeping a question keeps it on the order unanswered; only the clinician confirms it.
      for (const code of replacePrompt.orphanCodes) {
        if (!terms[code]) continue
        terms[code] = questions.has(code)
          ? { ...terms[code], origin: 'clinician', reviewState: 'needs-input' }
          : { ...terms[code], origin: 'clinician', categoryOwner: 'clinician', reviewState: 'active' }
      }
      commitReview(clinicalDiagnosis, { ...review, terms })
    }
    dropExtractionInFlight()
    setAnnouncement('Kept every phenotype.')
    setReplacePrompt(null)
    setReplaceBaseline(null)
    focusSummaryField()
  }

  const orphanCodes = clinicalDiagnosis
    .filter((term) => review?.terms[term.id]?.reviewState === 'not-in-summary')
    .map((term) => term.id)

  const decideOrphans = (decision: 'removed' | 'kept', fromKeyboard: boolean) => {
    if (orphanCodes.length === 0) return
    const before: ToastUndo = { kind: 'decision', clinicalDiagnosis, review, summary, extractionUndo: undo }
    dismissHoverTooltip()
    setSelectedPillId(null)
    if (decision === 'kept') {
      if (review) {
        const terms = { ...review.terms }
        // The clinician's now, so the next extraction does not report them as no longer in the summary again.
        for (const code of orphanCodes) {
          terms[code] = { ...terms[code], origin: 'clinician', categoryOwner: 'clinician', reviewState: 'active' }
        }
        commitReview(clinicalDiagnosis, { ...review, terms })
        // Remove all carries the extraction's Undo on its toast; Keep has no toast, so the status row keeps it.
        setUndo(undo)
      }
      // Keeping changes nothing on the order, so it gets no toast; the note unmounts under the keyboard focus.
      if (fromKeyboard) focusSummaryField()
      return
    }
    tombstoneCodes(orphanCodes)
    // The note and its buttons unmount; from the keyboard, Undo is the next Tab stop.
    setToast({
      message: clinicalSummaryOrphanRemovedToastMessage(orphanCodes.length),
      undo: before,
      takeFocus: fromKeyboard,
    })
  }

  const restoreExtractionSnapshot = async ({ reasons, ...snapshot }: ExtractionUndo) => {
    // A decision's Undo puts this snapshot back within reach of an edit that already scheduled a
    // generation beat, and the restore is the last word on the text: that beat would ask the
    // service about words the field no longer holds and report the restored note as pending.
    scheduling.cancelScheduled()
    requestSequence.current += 1
    activeRequest.current?.abort()
    setReveal(null)
    setUndoPending(true)
    try {
      await onUndoRestore(snapshot)
      setExtractionReasons(reasons.extractionReasons)
      setAnnotationReasons(reasons.annotationReasons)
      setContradictionCodes(reasons.contradictionCodes)
      setAcceptedText(snapshot.summary)
      setUndo(null)
      setReplacePrompt(null)
      setReplaceBaseline(null)
      setStatus('idle')
      setError(null)
    } catch {
      setStatus('idle')
      setError('The draft changed and could not be safely restored.')
    } finally {
      setUndoPending(false)
    }
  }

  const undoReplacement = async () => {
    if (!replaceBaseline) return
    scheduling.cancelScheduled()
    requestSequence.current += 1
    activeRequest.current?.abort()
    setReveal(null)
    setUndoPending(true)
    try {
      await onUndoRestore({
        summary: replaceBaseline.summary,
        clinicalDiagnosis: replaceBaseline.clinicalDiagnosis,
        review: replaceBaseline.review,
      })
      lastEmittedSummary.current = replaceBaseline.summary
      setAcceptedText(replaceBaseline.summary)
      setUndo(null)
      setReplacePrompt(null)
      setReplaceBaseline(null)
      setStatus('idle')
      setError(null)
      setAnnouncement('Restored the previous summary.')
      focusFieldWhenEnabled.current = true
    } catch {
      setStatus('idle')
      setError('The draft changed and could not be safely restored.')
    } finally {
      setUndoPending(false)
    }
  }

  const reviewRows = useMemo(
    () => phenotypeReviewRows(clinicalDiagnosis, review, knownLabels),
    [clinicalDiagnosis, review, knownLabels]
  )
  // Citations index `acceptedText`, the last extracted text, not the text being edited.
  const [editTrail, setEditTrail] = useState(() => extendEditTrail([], acceptedText, summary))
  const currentEditTrail = extendEditTrail(editTrail, acceptedText, summary)
  if (currentEditTrail !== editTrail) setEditTrail(currentEditTrail)
  latestEditTrail.current = currentEditTrail
  const highlightSpans = useMemo(
    // The fallback is built against `acceptedText`, not `summary`: these citations go through
    // the same remap as a real extraction's, which indexes the text the spans were built from.
    // Locating the labels in the edited text instead would offset every mark by the edit.
    () =>
      remapSpansAlongTrail(
        buildSummaryHighlightSpans(
          acceptedText,
          clinicalDiagnosis,
          review ?? reviewForUnreviewedDiagnosis(clinicalDiagnosis, acceptedText)
        ),
        currentEditTrail
      ),
    [acceptedText, currentEditTrail, clinicalDiagnosis, review]
  )
  /** A removed phrase describes no term on the order, so it opens no tooltip. */
  const describedSpans = useMemo(() => highlightSpans.filter((span) => span.tone !== 'removed'), [highlightSpans])
  // Rebuilt every render, so it never outlives the state it was derived from. Without it
  // the row sort would recompute each view twice per comparison.
  const needsInputRowViews = new Map<string, NeedsInputRowView>()
  const needsInputRowView = (term: CategorizedHPOOption): NeedsInputRowView => {
    const memoized = needsInputRowViews.get(term.id)
    if (memoized) return memoized
    const termReview = review?.terms[term.id]
    const state = termReview?.reviewState ?? 'needs-input'
    // Like the marks, quote the words on screen: a citation the edit touched is dropped.
    const liveCitations = remapSpansAlongTrail(termReview?.citations ?? [], currentEditTrail)
    // All three flag collections are keyed by the normalized (upper-case) code, so the
    // lookups have to normalize too — a lower-case id would silently miss every one.
    const flagKey = term.id.toUpperCase()
    const isContradiction = state === 'needs-input' && contradictionCodes.has(flagKey)
    const contextText = citationSentence(summary, liveCitations)
    const view: NeedsInputRowView = {
      snippet: citationSnippet(summary, liveCitations),
      reasonKind: resolveClinicalSummaryReason({
        termReview,
        category: term.category,
        contradiction: isContradiction,
        extractionReason: extractionReasons[flagKey] ?? null,
        contextText,
        annotationReason: flagKey in annotationReasons ? annotationReasons[flagKey] : undefined,
      }),
      citesRelative: citesRelativeFinding(
        contextText,
        liveCitations.map(({ begin, end }) => summary.slice(begin, end)).join(' ')
      ),
    }
    needsInputRowViews.set(term.id, view)
    return view
  }
  const needsInputTerms = useMemo(
    () => reviewRows.filter((term) => (review?.terms[term.id]?.reviewState ?? 'active') === 'needs-input'),
    [reviewRows, review]
  )
  const needsInputRows = orderNeedsInputRows(
    needsInputTerms,
    review,
    (term) => needsInputRowView(term).reasonKind,
    (term) => needsInputRowView(term).citesRelative
  )
  const readySections = useMemo(() => {
    // Terms no longer in the summary are still on the order, so they count as ready (R11, S05).
    const ready = reviewRows.filter((term) => {
      const state = review?.terms[term.id]?.reviewState ?? 'active'
      return state === 'active' || state === 'not-in-summary'
    })
    return model.sections.map((section) => ({
      section,
      terms: orderSectionChips(
        ready.filter((term) => section.categories.includes(term.category)),
        review,
        model.chipOrder
      ),
    }))
  }, [reviewRows, review, model])
  const readyCount = readySections.reduce((total, { terms }) => total + terms.length, 0)
  const needsCount = needsInputRows.length
  const rowEntries: { term: CategorizedHPOOption; view: NeedsInputRowView; settled: SettlingRow | null }[] =
    needsInputRows.map((term) => ({ term, view: needsInputRowView(term), settled: null }))
  for (const settled of [...settlingRows].sort((a, b) => a.index - b.index)) {
    // Asked again (an undo, a new extraction): the live row stands in for it.
    if (needsInputRows.some((term) => term.id === settled.term.id)) continue
    rowEntries.splice(Math.min(settled.index, rowEntries.length), 0, {
      term: settled.term,
      view: settled.view,
      settled,
    })
  }
  const decidedChipCodes = new Set(settlingRows.map((row) => row.term.id))
  latestPhenotypeTotal.current = readyCount + needsCount
  const willClearCodes = useMemo(() => new Set(replacePrompt?.orphanCodes ?? []), [replacePrompt])

  useEffect(() => {
    if (!reveal) return
    const timer = setTimeout(() => setReveal(null), reveal.durationMs)
    return () => clearTimeout(timer)
  }, [reveal])

  /** Entrance delay per new row and chip, in the order they render. */
  const rowRevealDelay = useMemo(() => {
    const delays = new Map<string, number>()
    if (!reveal) return delays
    const rendered = [...needsInputRows, ...readySections.flatMap(({ terms }) => terms)]
    for (const term of rendered) {
      if (reveal.codes.has(term.id) && !delays.has(term.id)) {
        delays.set(term.id, REVEAL_ROW_FIRST_DELAY_MS + delays.size * REVEAL_ROW_STAGGER_MS)
      }
    }
    return delays
  }, [reveal, needsInputRows, readySections])
  const rowReveal = (code: string) => {
    const delay = rowRevealDelay.get(code)
    return delay === undefined
      ? { className: '', style: undefined }
      : {
          className: ' motion-safe:animate-[clinical-summary-row-in_0.32s_backwards]',
          style: { animationDelay: `${delay}ms` },
        }
  }
  const removedEntries = useMemo(
    () =>
      (review?.tombstones ?? []).map((code) => ({
        id: code,
        label: knownLabels[code] ?? code,
      })),
    [review, knownLabels]
  )
  const removedTotal = removedEntries.length

  // Resolve tombstones saved without a label (older drafts). Search shares PhenoML's ~10/min limit
  // with Add phenotype, so lookups are paced, and after a failure they pause, then go on with the rest.
  useEffect(() => {
    const pending = (review?.tombstones ?? []).filter(
      (code) => !latestKnownLabels.current[code] && !attemptedTombstoneLabelIds.current.has(code)
    )
    if (pending.length === 0) return
    let active = true
    void (async () => {
      const getTermById = (id: string) => hpoAPIClient.getHPOTermById(id)
      for (const code of pending) {
        // Re-read after waiting: a lookup that was in flight when this run started may have failed since.
        for (let wait = nextTombstoneLookupAt.current - Date.now(); wait > 0 && active;) {
          await new Promise((resolve) => setTimeout(resolve, wait))
          wait = nextTombstoneLookupAt.current - Date.now()
        }
        if (!active) return
        if (latestKnownLabels.current[code]) continue
        // A code is tried once per session; one that fails keeps showing its HP id.
        attemptedTombstoneLabelIds.current.add(code)
        nextTombstoneLookupAt.current = Date.now() + TOMBSTONE_LOOKUP_SPACING_MS
        try {
          const resolved = await resolveHpoLabel(
            {
              id: code,
              label: code,
              category: 'Key',
              node: { id: code, lbl: code, type: 'PhenotypicFeature' },
            },
            getTermById
          )
          if (resolved.label && !isHpoCode(resolved.label)) {
            setRemovedLabels((prev) => (prev[code] === resolved.label ? prev : { ...prev, [code]: resolved.label }))
            // Persist the name onto the review, not just this session's chip text.
            // A reopened order's tombstones arrive without one — the server rebuilds
            // the review from its own rows and never stored a label — and the merge
            // locates a removed phrase in the summary by its label to re-draw the
            // dashed mark. Without this the term stays a bare HP code and is never
            // underlined again.
            //
            // Guarded on `active`: unlike the state setter above, this dispatches
            // into Redux, so a resolve that lands after teardown would write a
            // stale review back over the order.
            if (!active) return
            const current = latestReview.current
            const term = current?.terms?.[code]
            if (current && term && (!term.label || isHpoCode(term.label))) {
              onReviewChange(latestDiagnosis.current, {
                ...current,
                terms: { ...current.terms, [code]: { ...term, label: resolved.label } },
              })
            }
          }
        } catch {
          nextTombstoneLookupAt.current = Date.now() + TOMBSTONE_LOOKUP_PAUSE_MS
          if (!active) return
          if (tombstoneResumeTimer.current) clearTimeout(tombstoneResumeTimer.current)
          tombstoneResumeTimer.current = setTimeout(() => {
            tombstoneResumeTimer.current = null
            setTombstoneLookupResumes((resumes) => resumes + 1)
          }, TOMBSTONE_LOOKUP_PAUSE_MS)
          return
        }
      }
    })()
    return () => {
      active = false
    }
  }, [review?.tombstones, tombstoneLookupResumes])
  useEffect(
    () => () => {
      if (tombstoneResumeTimer.current) clearTimeout(tombstoneResumeTimer.current)
    },
    []
  )

  const tooltipLayout = CLINICAL_SUMMARY_TOOLTIP_LAYOUT[phenotypeModel]
  // Citations index `acceptedText`; carry them through the edits since, like the marks.
  const hoverTooltipModel = useMemo(() => {
    if (!hoverTooltip) return null
    const term = reviewRows.find((item) => item.id === hoverTooltip.code)
    if (!term) return null
    return buildClinicalSummaryTooltipModelForTerm(term, summary, review, {
      includeContextSnippet: tooltipLayout[hoverTooltip.variant].quote,
      citations: remapSpansAlongTrail(review?.terms[term.id]?.citations ?? [], currentEditTrail),
      categoryLabels: model.tooltipLabels,
    })
  }, [hoverTooltip, reviewRows, summary, review, currentEditTrail, tooltipLayout, model])
  const describedByTooltip = (variant: ClinicalSummaryHoverTooltipVariant, code?: string) =>
    hoverTooltipModel && hoverTooltip?.variant === variant && (code === undefined || hoverTooltip.code === code)
      ? hoverTooltipId
      : undefined

  useEffect(() => {
    if (!hoverTooltip) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      // Only the tooltip closes; the next Escape reaches whatever is underneath it.
      event.stopPropagation()
      setHoverTooltip(null)
    }
    // The pointer has left whatever it hovered; a keyboard-opened tooltip follows its anchor instead,
    // once the backdrop has caught up with the textarea's scroll.
    const onScroll = () => {
      setHoverTooltip((current) => (current?.source === 'focus' ? current : null))
      window.requestAnimationFrame(() =>
        setHoverTooltip((current) => {
          if (!current?.anchorElement) return current
          if (!current.anchorElement.isConnected) return null
          const anchor = anchorRect(current.anchorElement)
          const field = summaryTextareaRef.current?.getBoundingClientRect()
          const phraseHidden =
            current.variant === 'highlight' && field && (anchor.bottom < field.top || anchor.top > field.bottom)
          return phraseHidden ? null : { ...current, anchor }
        })
      )
    }
    window.addEventListener('keydown', onKeyDown, true)
    window.addEventListener('scroll', onScroll, { capture: true, passive: true })
    return () => {
      window.removeEventListener('keydown', onKeyDown, true)
      window.removeEventListener('scroll', onScroll, { capture: true })
    }
  }, [hoverTooltip])

  // A chip's inline category control or menu closes on Escape and on a press anywhere outside its chip.
  useEffect(() => {
    if (selectedPillId === null) return
    const onKeyDown = (event: KeyboardEvent) => {
      // An open tooltip takes the first Escape, and the add popover sits above the chips.
      if (event.key !== 'Escape' || hoverTooltip || searchOpen) return
      event.stopPropagation()
      const trigger = document.getElementById(`${chipIdBase}-chip-${selectedPillId}`)
      if (trigger?.closest('[data-chip-control]')?.contains(document.activeElement)) trigger.focus()
      setSelectedPillId(null)
    }
    const onMouseDown = (event: MouseEvent) => {
      if (event.target instanceof Element && event.target.closest('[data-chip-control="open"]')) return
      setSelectedPillId(null)
    }
    window.addEventListener('keydown', onKeyDown, true)
    document.addEventListener('mousedown', onMouseDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown, true)
      document.removeEventListener('mousedown', onMouseDown)
    }
  }, [selectedPillId, hoverTooltip, searchOpen, chipIdBase])

  useEffect(() => {
    const pending = chipToFocus.current
    if (!pending) return
    chipToFocus.current = null
    document.getElementById(`${chipIdBase}-chip-${pending.code}`)?.focus({ preventScroll: pending.preventScroll })
  })

  useEffect(
    () => () => {
      for (const timer of settleTimers.current) clearTimeout(timer)
    },
    []
  )

  /**
   * A settle beat, tracked only until it fires: the timer drops its own id first, so an early return
   * inside `step` cannot leave it behind and unmount only ever clears beats that are still pending.
   */
  const scheduleSettleStep = useCallback((step: () => void, delayMs: number) => {
    const timer = setTimeout(() => {
      settleTimers.current.delete(timer)
      step()
    }, delayMs)
    settleTimers.current.add(timer)
  }, [])

  useEffect(() => {
    if (!shownPhrase) return
    const timer = setTimeout(() => setShownPhrase(null), SHOWN_PHRASE_RING_MS)
    return () => clearTimeout(timer)
  }, [shownPhrase])

  const showRowTooltip = (term: CategorizedHPOOption, anchor: HTMLElement) => {
    setHoverTooltip({ code: term.id, variant: 'row', anchor: anchorRect(anchor), source: 'pointer' })
  }

  const showChipTooltip = (term: CategorizedHPOOption, anchor: HTMLElement, source: HoverTooltip['source']) => {
    // An open chip control sits where the tooltip would.
    if (selectedPillId !== null) return
    setHoverTooltip({
      code: term.id,
      variant: 'chip',
      anchor: anchorRect(anchor),
      source,
      anchorElement: source === 'focus' ? anchor : undefined,
    })
  }

  const showHighlightTooltip = (span: SummaryHighlightSpan, source: HoverTooltip['source'], clientY?: number) => {
    if (hoverTooltip?.variant === 'highlight' && hoverTooltip.begin === span.begin && hoverTooltip.source === source) {
      return
    }
    const mark = summaryBackdropRef.current?.querySelector<HTMLElement>(`mark[data-begin="${span.begin}"]`)
    if (!mark) return
    // The pointer's line box of a wrapped phrase; the caret's tooltip uses the first.
    const line =
      clientY === undefined
        ? undefined
        : [...mark.getClientRects()].find((rect) => clientY >= rect.top && clientY <= rect.bottom)
    setHoverTooltip({
      code: span.code,
      variant: 'highlight',
      anchor: line ? { top: line.top, bottom: line.bottom, left: line.left } : anchorRect(mark),
      source,
      begin: span.begin,
      anchorElement: source === 'focus' ? mark : undefined,
    })
  }

  const dismissHighlightTooltip = (source: HoverTooltip['source']) => {
    if (hoverTooltip?.variant === 'highlight' && hoverTooltip.source === source) dismissHoverTooltip()
  }

  /** Textarea sits above the highlight backdrop, so mark hover cannot receive events. */
  const onSummaryMouseMove = (event: ReactMouseEvent<HTMLTextAreaElement>) => {
    const offset =
      status === 'generating' || describedSpans.length === 0
        ? -1
        : textareaOffsetFromPoint(event.currentTarget, event.clientX, event.clientY)
    const span = describedSpans.find((item) => offset >= item.begin && offset < item.end)
    if (span) showHighlightTooltip(span, 'pointer', event.clientY)
    else dismissHighlightTooltip('pointer')
  }

  /** A caret inside a highlighted phrase shows its tooltip, for keyboard and screen-reader users. */
  const onSummarySelect = (event: SyntheticEvent<HTMLTextAreaElement>) => {
    const { selectionStart, selectionEnd } = event.currentTarget
    const span =
      status === 'generating' || selectionStart !== selectionEnd
        ? undefined
        : describedSpans.find((item) => selectionStart > item.begin && selectionStart < item.end)
    if (span) showHighlightTooltip(span, 'focus')
    else dismissHighlightTooltip('focus')
  }

  /** The decided row keeps its slot, settled, while its chip and the counts already show the decision. */
  const decideRow = (term: CategorizedHPOOption, category: CategorizedHPOOption['category'], fromKeyboard = false) => {
    const index = needsInputRows.findIndex((row) => row.id === term.id)
    const view = needsInputRowView(term)
    changeCategory(term.id, category, { fromKeyboard })
    if (!model.settlesDecidedRow || prefersReducedMotion()) return
    const key = ++settlingKey.current
    setSettlingRows((rows) => [
      ...rows.filter((row) => row.term.id !== term.id),
      { key, term, view, category, index, leaving: false },
    ])
    scheduleSettleStep(
      () => setSettlingRows((rows) => rows.map((row) => (row.key === key ? { ...row, leaving: true } : row))),
      SETTLE_MS
    )
    scheduleSettleStep(
      () => setSettlingRows((rows) => rows.filter((row) => row.key !== key)),
      SETTLE_MS + SETTLE_EXIT_MS
    )
  }

  /** The phrase a chip's tooltip quotes, where it is on screen now. */
  const quotedPhrase = (term: CategorizedHPOOption) =>
    quotedCitation(summary, remapSpansAlongTrail(review?.terms[term.id]?.citations ?? [], currentEditTrail), term.label)

  const showInSummary = (term: CategorizedHPOOption, fromKeyboard: boolean) => {
    setSelectedPillId(null)
    dismissHoverTooltip()
    const quoted = quotedPhrase(term)
    if (!quoted) return
    setShownPhrase({ code: term.id, at: quoted.begin })
    // Back to the chip without scrolling away from the phrase.
    if (fromKeyboard) chipToFocus.current = { code: term.id, preventScroll: true }
    const span = highlightSpans.find(
      (item) => item.code === term.id && item.begin <= quoted.begin && quoted.begin < item.end
    )
    summaryBackdropRef.current
      ?.querySelector(`mark[data-begin="${span?.begin}"]`)
      ?.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
  }

  const linkedMarks: LinkedSummaryMark[] = []
  if (hoverTooltip && hoverTooltipModel) {
    if (hoverTooltip.variant === 'highlight') {
      linkedMarks.push({ code: hoverTooltip.code })
    } else if (hoverTooltip.variant === 'chip' && model.chipHoverRingsQuote && hoverTooltipModel.quotedBegin !== null) {
      linkedMarks.push({ code: hoverTooltip.code, at: hoverTooltipModel.quotedBegin })
    }
  }
  if (shownPhrase) linkedMarks.push(shownPhrase)

  /**
   * The chip's own keys: the arrows open its menu on an end, and Delete or Backspace removes it
   * outright. Removal is undoable from the toast, so it needs no confirmation.
   */
  const onChipKeyDown = (
    event: ReactKeyboardEvent<HTMLButtonElement>,
    code: string,
    neighbourCode: string | undefined
  ) => {
    if (event.key === 'Delete' || event.key === 'Backspace') {
      // A chip is not a text field, so Backspace would otherwise be the browser's Back.
      event.preventDefault()
      removeTerm(code, { fromKeyboard: true })
      // The chip leaves with the focus on it; the same handoff the menu's Remove makes.
      if (neighbourCode) chipToFocus.current = { code: neighbourCode, preventScroll: false }
      return
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
    event.preventDefault()
    // The menu mounts under this same chip, so without stopping here the very keystroke that
    // opened it also reaches the menu's own arrow handler and steps one item past the edge.
    event.stopPropagation()
    dismissHoverTooltip()
    setChipMenuFocus({ edge: event.key === 'ArrowDown' ? 'first' : 'last' })
    setSelectedPillId(code)
  }

  const phenotypeBadge = (() => {
    if (readyCount === 0 && needsCount === 0) return { label: '0 found', tone: 'gray' as const }
    if (needsCount > 0 && readyCount > 0) {
      return {
        label: `${readyCount} ready, ${needsCount} need your input`,
        tone: 'warning' as const,
      }
    }
    if (needsCount > 0) {
      return { label: `${needsCount} need your input`, tone: 'warning' as const }
    }
    // Figma R06/R13 settled-all-ready: success pill with check + "N ready".
    return { label: `${readyCount} ready`, tone: 'success' as const }
  })()
  const phenotypeTotal = readyCount + needsCount
  const badgeCountUp =
    reveal && phenotypeTotal > 0
      ? {
          id: reveal.id,
          from: reveal.countFromZero ? 0 : Math.max(0, phenotypeTotal - reveal.codes.size),
          to: phenotypeTotal,
          durationMs: reveal.countMs,
        }
      : null

  const syncSummaryScroll = useCallback(() => {
    const backdrop = summaryBackdropRef.current
    const textarea = summaryTextareaRef.current
    if (!backdrop || !textarea) return
    backdrop.scrollTop = textarea.scrollTop
    backdrop.scrollLeft = textarea.scrollLeft
  }, [])

  useEffect(() => {
    syncSummaryScroll()
  }, [summary, highlightSpans, syncSummaryScroll])

  // The field grows with its text rather than scrolling inside, so the highlight backdrop never scrolls either.
  const autoSizeSummary = useCallback(() => {
    const textarea = summaryTextareaRef.current
    if (!textarea) return
    textarea.style.height = 'auto'
    textarea.style.height = `${textarea.scrollHeight + textarea.offsetHeight - textarea.clientHeight}px`
  }, [])

  useLayoutEffect(() => {
    autoSizeSummary()
  }, [summary, autoSizeSummary])

  // Rewrapping (a narrower window, the web font arriving) changes the height without a new value.
  useEffect(() => {
    const textarea = summaryTextareaRef.current
    if (!textarea) return
    void document.fonts?.ready.then(autoSizeSummary)
    if (typeof ResizeObserver === 'undefined') return
    let width = textarea.clientWidth
    const observer = new ResizeObserver(() => {
      if (textarea.clientWidth === width) return
      width = textarea.clientWidth
      autoSizeSummary()
    })
    observer.observe(textarea)
    return () => observer.disconnect()
  }, [autoSizeSummary])

  const categoryButton = (
    termId: string,
    option: ClinicalSummaryPhenotypeOption,
    current: CategorizedHPOOption['category'] | null,
    choose: (category: CategorizedHPOOption['category'], fromKeyboard: boolean) => void = (category, fromKeyboard) =>
      changeCategory(termId, category, { fromKeyboard }),
    settled = false
  ) => {
    const selected = current !== null && option.covers.includes(current)
    return (
      <button
        key={option.category}
        type="button"
        disabled={undoPending}
        aria-pressed={selected}
        aria-disabled={settled || undefined}
        tabIndex={settled ? -1 : undefined}
        onClick={(event) => {
          if (!settled) choose(option.category, event.detail === 0)
        }}
        className={`focus-visible:ring-brand-600 -ml-px inline-flex h-[26px] cursor-pointer items-center justify-center whitespace-nowrap border border-border-base dark:border-gray-700 px-3 text-xs font-medium leading-4 shadow-xs transition-colors duration-[180ms] first:ml-0 first:rounded-l-full last:rounded-r-full focus-visible:relative focus-visible:outline-none focus-visible:ring-2 active:bg-background-brand-soft dark:active:bg-gray-800 active:text-text-fg-brand dark:active:text-brand-400 disabled:cursor-not-allowed motion-reduce:transition-none ${option.widthClass} ${
          selected ? 'bg-background-brand-soft dark:bg-gray-800 text-text-fg-brand dark:text-brand-400' : 'bg-white dark:bg-gray-900 text-text-body dark:text-gray-400 hover:bg-background-secondary-medium dark:hover:bg-gray-800'
        }`}
      >
        {option.label}
      </button>
    )
  }

  const toastUndo = toast?.undo
  const toastAction: ClinicalSummaryToastAction | null =
    toastUndo && (toastUndo.kind !== 'extraction' || undo)
      ? {
          label: 'Undo',
          onAction: () => {
            void undoFromToast(toastUndo)
          },
        }
      : null

  // Rady draws the prompt under the summary field; SeqOne inside the Phenotypes block, naming the clinician's own terms.
  const replacePromptElement = replacePrompt ? (
    <ClinicalSummaryReplacePrompt
      orphanCount={replacePrompt.orphanCount}
      keptCount={phenotypeModel === 'seqone' ? replacePrompt.keptCount : 0}
      onClearOrphans={clearReplaceOrphans}
      onKeepEverything={keepReplaceEverything}
      onUndoReplacement={() => {
        void undoReplacement()
      }}
    />
  ) : null

  const orphanNoteElement =
    orphanCodes.length > 0 && status !== 'generating' ? (
      <ClinicalSummaryOrphanNote
        count={orphanCodes.length}
        disabled={undoPending}
        onRemoveAll={(event) => decideOrphans('removed', event.detail === 0)}
        onKeep={(event) => decideOrphans('kept', event.detail === 0)}
      />
    ) : null

  /**
   * The summary field: highlight backdrop, textarea and generation shimmer.
   *
   * Held in a variable because the attachments uploader wraps it to make the
   * field itself the drop target, and it must render identically when the
   * uploader is absent or its chunk has not arrived.
   */
  const summaryHeading = <h2 className={SECTION_HEADING_CLASS}>Clinical Summary</h2>
  const summaryLimitNotice = overCombinedLimit ? (
    <p role="alert" className="text-sm text-text-fg-error dark:text-error-100">
      {clinicalTextBudget?.kind === 'shared'
        ? SHARED_CLINICAL_TEXT_TOO_LONG_MESSAGE
        : `The combined clinical summary, differential diagnosis, prior genetic testing, and family history exceed the ${(clinicalTextBudget?.limit ?? 0).toLocaleString()}-character limit.`}
    </p>
  ) : null
  const summaryField = (
    <div
      onPaste={() => {
        pastePending.current = true
      }}
      onBlurCapture={scheduling.blur}
      onKeyDown={(event) => {
        if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
          event.preventDefault()
          scheduling.generate()
        }
      }}
    >
      <div className="relative overflow-hidden rounded-base">
        <div
          ref={summaryBackdropRef}
          aria-hidden="true"
          className={`pointer-events-none absolute inset-0 z-0 overflow-hidden whitespace-pre-wrap break-words rounded-base border border-transparent px-[18px] py-4 font-sans text-base font-normal leading-6 tracking-normal text-transparent ${
            status === 'generating' ? 'bg-transparent' : 'bg-white dark:bg-gray-900'
          }`}
        >
          <HighlightedSummaryBackdrop
            summary={summary}
            spans={status === 'generating' ? [] : highlightSpans}
            revealCodes={reveal?.codes}
            linked={linkedMarks}
          />
        </div>
        <textarea
          ref={summaryTextareaRef}
          aria-label="Clinical Summary"
          aria-describedby={describedByTooltip('highlight')}
          value={summary}
          rows={1}
          disabled={undoPending}
          onScroll={syncSummaryScroll}
          onMouseMove={onSummaryMouseMove}
          onMouseLeave={() => dismissHighlightTooltip('pointer')}
          onSelect={onSummarySelect}
          onBlur={() => dismissHighlightTooltip('focus')}
          onChange={(event) => {
            if (undoPending) return
            commitSummaryText(event.target.value, pastePending.current ? 'paste' : 'typing')
            pastePending.current = false
          }}
          className={`relative z-[1] block w-full resize-none overflow-hidden whitespace-pre-wrap break-words rounded-base border-[1.5px] bg-transparent px-[18px] py-4 font-sans text-base font-normal leading-6 tracking-normal text-text-heading dark:text-white caret-brand-800 dark:caret-brand-400 [box-shadow:none] placeholder:text-text-subtle dark:placeholder:text-gray-500 focus:outline-none ${
            summary ? '' : 'min-h-[180px]'
          } ${
            overCombinedLimit
              ? 'border-error-500 focus:border-error-600'
              : status === 'generating'
                ? 'border-brand-600 dark:border-brand-500'
                : 'border-border-base dark:border-gray-700'
          }`}
          placeholder={SUMMARY_PLACEHOLDER}
        />
        <ClinicalSummaryGeneratingShimmer active={status === 'generating'} />
      </div>
    </div>
  )

  return (
    <section
      ref={editorRef}
      aria-label="Clinical Summary editor"
      aria-busy={undoPending || status === 'generating'}
      className="flex flex-col gap-6"
    >
      <ClinicalSummaryInfoBanner />
      <ClinicalSummaryDecisionToast
        message={toast?.message ?? null}
        action={toastAction}
        onDismiss={dismissDecisionToast}
        durationMs={toast?.durationMs}
        takeFocus={toast?.takeFocus}
        onFocusReturn={returnFocusFromToast}
      />
      <p className="sr-only" aria-live="polite">
        {announcement}
      </p>
      <ClinicalSummaryHoverTooltipPortal
        id={hoverTooltipId}
        anchor={hoverTooltipModel ? (hoverTooltip?.anchor ?? null) : null}
        model={hoverTooltipModel}
        layout={tooltipLayout[hoverTooltip?.variant ?? 'highlight']}
      />

      <div className="flex flex-col gap-3">
        {attachmentsReferenceId ? (
          // The plain heading and field are the fallback, so a slow chunk never
          // blanks the summary; and with the feature off the chunk is not
          // requested at all.
          <Suspense
            fallback={
              <>
                {summaryHeading}
                {summaryLimitNotice}
                {summaryField}
              </>
            }
          >
            <ClinicalNoteAttachments
              referenceId={attachmentsReferenceId}
              heading={summaryHeading}
              notice={summaryLimitNotice}
              actionClassName={HEADER_BUTTON_CLASS}
              onTextExtracted={(text) => {
                // Append, never overwrite: the clinician's own words come first and an
                // attachment is additive. Routed through the normal edit path so the
                // appended text is extracted and reviewed like anything they typed.
                const addition = text.trim()
                if (!addition) return
                const current = latestSummary.current
                commitSummaryText(current.trim() ? `${current.trimEnd()}\n\n${addition}` : addition, 'paste')
              }}
            >
              {summaryField}
            </ClinicalNoteAttachments>
          </Suspense>
        ) : (
          <>
            {summaryHeading}
            {summaryLimitNotice}
            {summaryField}
          </>
        )}

        {model.orphanNote === 'under-field' ? orphanNoteElement : null}

        {phenotypeModel === 'rady' ? replacePromptElement : null}

        {clinicalTextBudget ? (
          <div className="flex items-start gap-3 text-xs leading-5 text-text-body-subtle dark:text-gray-500">
            {status !== 'generating' ? (
              <p>
                {clinicalTextBudget.kind === 'shared'
                  ? SHARED_CLINICAL_TEXT_HELPER
                  : 'Count includes differential diagnosis, prior genetic testing, and family history.'}
              </p>
            ) : null}
            <p
              className={`ml-auto shrink-0 font-medium tabular-nums ${overCombinedLimit ? 'text-error-600' : ''}`}
              aria-live="polite"
              data-testid="clinical-summary-combined-counter"
            >
              {combinedLength.toLocaleString()} / {clinicalTextBudget.limit.toLocaleString()}
            </p>
          </div>
        ) : null}

        {/*
          Figma Specs (Dot states, Fallback link): the status line exists only in the pending and
          working states. There is no settled dot and no settled copy — once the phenotypes are
          current the line is removed, and the green "N ready" badge is what says so. Two escape
          hatches keep it: a field whose extraction is manual can only be generated from here, and
          an undo needs somewhere to sit. Neither of those shows under an empty field.
        */}
        {/*
          Figma Specs (Dot states, Fallback link): the status line exists only in the pending and
          working states. There is no settled dot and no settled copy — once the phenotypes are
          current the line is removed, and the green "N ready" badge is what says so. No frame of
          either lab draws a settled row, so a finished extraction's Undo cannot hold it open; it
          rides the toast the labs that trim already raise. The hatch that stays is a field whose
          extraction is manual: Generate now is the only way to start one.

          Rendered conditionally rather than with `hidden`: the row's own `flex` display would win
          over the attribute's `display: none`, so it would stay on screen in every state.
        */}
        {status === 'pending' ||
        status === 'generating' ||
        status === 'failed' ||
        (buttonOnly && Boolean(summary.trim())) ? (
          <div className="flex flex-wrap items-center gap-3 text-xs leading-5">
            <span aria-live="polite" className="inline-flex items-center gap-1.5 text-text-body dark:text-gray-400">
              {status === 'generating' ? (
                <ClinicalSummaryGeneratingStatus key={generationRun} />
              ) : status === 'pending' ? (
                <>
                  <span className="size-2 shrink-0 rounded-full bg-orange-500" />
                  {buttonOnly ? 'Changes pending generation' : 'Phenotypes update when you pause'}
                </>
              ) : status === 'failed' ? (
                <>
                  <span className="size-2 shrink-0 rounded-full bg-error-600" />
                  Generation failed
                </>
              ) : null}
            </span>
            <div className="ml-auto flex items-center gap-3">
              {/* Manual escape hatch (also required when automatic extraction is disabled). */}
              {status === 'generating' || (!summary.trim() && !buttonOnly) ? null : (
                <button
                  type="button"
                  onClick={scheduling.generate}
                  disabled={!summary.trim() || undoPending}
                  className="font-medium text-brand-600 dark:text-brand-400 disabled:opacity-50"
                >
                  Generate now
                </button>
              )}
              {undo && toast?.undo?.kind !== 'extraction' ? (
                <button
                  type="button"
                  className="font-medium text-brand-600 dark:text-brand-400"
                  disabled={undoPending}
                  onClick={() => {
                    void restoreExtractionSnapshot(undo)
                  }}
                >
                  {undoPending ? 'Undoing…' : 'Undo'}
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
        {buttonOnly ? (
          <p className="text-sm text-text-body-subtle dark:text-gray-500">
            Automatic generation is unavailable. Use Generate now when the summary is ready.
          </p>
        ) : null}
        {error ? (
          <p role="alert" className="text-sm text-text-fg-error dark:text-error-100">
            {error}
          </p>
        ) : null}
      </div>

      <hr className="border-0 border-t border-border-base dark:border-gray-700" />

      <div className="flex flex-col gap-3" data-testid="clinical-summary-phenotypes">
        <div className="flex flex-wrap items-center gap-2.5">
          <h3 className={SECTION_HEADING_CLASS}>Phenotypes</h3>
          <ClinicalSummaryPhenotypeBadge
            generating={status === 'generating'}
            label={phenotypeBadge.label}
            tone={phenotypeBadge.tone}
            countUp={badgeCountUp}
          />
          <div className="min-w-0 flex-1" />
          <div className="relative flex">
            <button
              ref={addButtonRef}
              type="button"
              disabled={undoPending}
              aria-haspopup="dialog"
              aria-expanded={searchOpen}
              onClick={() => {
                // The popover owns Escape while open, so nothing underneath may still be waiting for one.
                setSelectedPillId(null)
                dismissHoverTooltip()
                setSearchOpen((open) => !open)
              }}
              className={HEADER_BUTTON_CLASS}
            >
              Add phenotype
            </button>
            {searchOpen ? (
              <ClinicalSummaryAddPhenotypePopover
                anchorRef={addButtonRef}
                addActions={model.addActions}
                onOrderCodes={new Set(clinicalDiagnosis.map((term) => term.id))}
                onAdd={addTerm}
                onClose={(reason) => {
                  setSearchOpen(false)
                  if (reason === 'escape') addButtonRef.current?.focus()
                }}
              />
            ) : null}
          </div>
        </div>

        {phenotypeModel === 'seqone' ? replacePromptElement : null}

        {reviewRows.length === 0 && removedTotal === 0 ? (
          <p className="rounded-base bg-background-secondary-medium dark:bg-gray-800 px-4 py-5 text-center text-sm leading-5 text-text-subtle dark:text-gray-500">
            Phenotypes appear here as you write, with the words they came from.
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {rowEntries.length > 0 ? (
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-medium leading-4 text-orange-700 dark:text-orange-200">Needs your input</h4>
                  {needsCount > 0 ? (
                    <span className={`${PILL_SHELL_CLASS} min-w-5 ${WARNING_PILL_TONE_CLASS}`}>{needsCount}</span>
                  ) : null}
                </div>
                <div className="overflow-hidden rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900">
                  {rowEntries.map(({ term, view, settled }) => {
                    const { snippet, reasonKind } = view
                    // Figma needs-input rows show an unselected category control until the clinician picks.
                    const selectedCategory = settled ? settled.category : null
                    const entrance = settled ? { className: '', style: undefined } : rowReveal(term.id)
                    const choose = (category: CategorizedHPOOption['category'], fromKeyboard: boolean) =>
                      decideRow(term, category, fromKeyboard)
                    return (
                      // A settled row only echoes the decision the toast announces: nothing in it is a control any more.
                      <div
                        key={settled ? `settled-${settled.key}` : term.id}
                        data-testid="clinical-summary-needs-input-row"
                        data-settled-row={settled ? term.id : undefined}
                        aria-hidden={settled ? true : undefined}
                        className={`items-start border-border-base dark:border-gray-700 pl-2.5 pr-1.5 last:border-b-0${entrance.className} ${
                          settled
                            ? // The row collapses where it stands: a one-row grid going 1fr → 0fr
                              // takes its height with it, and the padding and the divider go at the
                              // same time, so the rows below simply move up. Grid items only shrink
                              // past their content once they stop being `overflow: visible`, hence
                              // the child clipping. Nothing is measured and nothing is deferred, so
                              // there is no second move after the fade.
                              `grid grid-cols-[minmax(0,1fr)_auto] overflow-hidden transition-all duration-[320ms] ease-out motion-reduce:transition-none [&>*]:min-h-0 [&>*]:overflow-hidden ${
                                settled.leaving ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] border-b py-2'
                              }`
                            : 'flex border-b py-2'
                        }`}
                        style={entrance.style}
                      >
                        <div
                          className="flex min-w-0 flex-1 flex-col gap-1.5"
                          onMouseEnter={settled ? undefined : (event) => showRowTooltip(term, event.currentTarget)}
                          onMouseLeave={settled ? undefined : dismissHoverTooltip}
                        >
                          <div className="flex min-h-5 flex-wrap items-center gap-2">
                            <p className="text-sm font-medium leading-5 text-text-heading dark:text-white">{term.label}</p>
                            <p className="text-xs leading-5 text-text-subtle dark:text-gray-500">{term.id}</p>
                            {reasonKind ? (
                              <span className={`${PILL_SHELL_CLASS} ${WARNING_PILL_TONE_CLASS}`}>
                                {clinicalSummaryReasonLabel(reasonKind)}
                              </span>
                            ) : null}
                          </div>
                          {snippet ? (
                            <p
                              className={`border-l-2 border-orange-500 dark:border-orange-700 py-px pl-2.5 text-sm leading-5 text-text-body dark:text-gray-400${settled ? ' opacity-35' : ''}`}
                            >
                              {snippet.before}
                              <mark className="rounded-[3px] bg-yellow-200 dark:bg-yellow-900/40 p-0.5 text-base leading-6 text-text-heading dark:text-white shadow-[inset_0_0_0_1.5px_var(--color-orange-500)]">
                                {snippet.match}
                              </mark>
                              {snippet.after}
                            </p>
                          ) : null}
                        </div>
                        <div className="mr-2.5 flex flex-wrap items-start">
                          <div
                            className="flex"
                            role={settled ? undefined : 'group'}
                            aria-label={settled ? undefined : `Set category for ${term.label}`}
                          >
                            {model.options.map((option) =>
                              categoryButton(term.id, option, selectedCategory, choose, Boolean(settled))
                            )}
                          </div>
                          <button
                            type="button"
                            disabled={undoPending}
                            aria-disabled={settled ? true : undefined}
                            tabIndex={settled ? -1 : undefined}
                            onClick={() => {
                              if (!settled) removeTerm(term.id)
                            }}
                            aria-label={`Remove ${term.label}`}
                            className="inline-flex size-7 cursor-pointer items-center justify-center rounded-base text-text-subtle dark:text-gray-500 hover:bg-background-secondary-medium dark:hover:bg-gray-800 hover:text-text-heading dark:hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <Close className="size-3.5" aria-hidden />
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ) : null}

            {model.orphanNote === 'above-chips' ? orphanNoteElement : null}

            {readyCount > 0 ? (
              <div className="flex flex-col gap-7">
                {readySections.map(({ section, terms }) =>
                  terms.length > 0 ? (
                    <div key={section.key} className="flex flex-col gap-2">
                      <div className="flex items-center gap-1.5 text-xs font-semibold leading-4 text-text-body dark:text-gray-400">
                        <span className={`size-[9px] shrink-0 rounded-xs ${section.dotClass}`} />
                        {section.label}
                        {/*
                          Keyed by the count so React replaces the node and the pop plays again
                          every time a decision moves a phenotype into or out of this section.
                        */}
                        <span
                          key={terms.length}
                          className="font-medium text-text-subtle dark:text-gray-500 motion-safe:animate-[clinical-summary-pop_0.32s_ease-out]"
                        >
                          {terms.length}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {terms.map((term, termIndex) => {
                          const selected = selectedPillId === term.id
                          const reveal = rowReveal(term.id)
                          const entrance =
                            reveal.className || !decidedChipCodes.has(term.id)
                              ? reveal
                              : { className: decidedChipEnterClass(term.category), style: undefined }
                          const neighbour = terms[termIndex + 1] ?? terms[termIndex - 1]
                          const orphan = review?.terms[term.id]?.reviewState === 'not-in-summary'
                          const orphanCueId = `${chipTriggerId(term.id)}-orphan`
                          // From the stored origin, not the section, so it reads the same in either lab's sections.
                          const addedByClinician = review?.terms[term.id]?.origin === 'clinician'
                          const addedCueId = `${chipTriggerId(term.id)}-added`
                          return (
                            <div
                              key={term.id}
                              data-chip-control={selected ? 'open' : undefined}
                              className={`relative flex${selected && !model.chipMenu ? ' mb-[30px]' : ''}${entrance.className}`}
                              style={entrance.style}
                              onBlur={
                                model.chipMenu && selected
                                  ? (event) => {
                                      // Tabbing past the chip and its menu closes the menu.
                                      if (
                                        event.relatedTarget instanceof Node &&
                                        !event.currentTarget.contains(event.relatedTarget)
                                      ) {
                                        setSelectedPillId(null)
                                      }
                                    }
                                  : undefined
                              }
                            >
                              <div
                                className={`${model.chipMenu ? 'relative px-[11px]' : 'pl-[11px] pr-1'} ${addedByClinician ? 'gap-[5px]' : 'gap-1'} ${
                                  // A ring is a shadow, so hover, focus and the open menu all read as an
                                  // outline without resizing the chip — the row can never rewrap under the pointer.
                                  selected ? 'ring-2' : ''
                                } ring-brand-600/40 has-[:focus-visible]:ring-brand-600 inline-flex h-[30px] items-center rounded-full border py-0 text-sm font-medium leading-5 hover:ring-2 has-[:focus-visible]:ring-2 ${
                                  willClearCodes.has(term.id)
                                    ? WILL_CLEAR_CHIP_CLASS
                                    : orphan
                                      ? orphanChipClass
                                      : model.chipClass(term.category)
                                }`}
                                onMouseEnter={(event) => showChipTooltip(term, event.currentTarget, 'pointer')}
                                onMouseLeave={dismissHoverTooltip}
                              >
                                {addedByClinician ? <User className="size-[13px] shrink-0" aria-hidden /> : null}
                                <button
                                  id={chipTriggerId(term.id)}
                                  type="button"
                                  disabled={undoPending}
                                  aria-describedby={
                                    [
                                      addedByClinician ? addedCueId : undefined,
                                      orphan ? orphanCueId : undefined,
                                      describedByTooltip('chip', term.id),
                                    ]
                                      .filter(Boolean)
                                      .join(' ') || undefined
                                  }
                                  aria-haspopup={model.chipMenu ? 'menu' : undefined}
                                  aria-expanded={model.chipMenu ? selected : undefined}
                                  aria-controls={
                                    model.chipMenu && selected ? `${chipTriggerId(term.id)}-menu` : undefined
                                  }
                                  onKeyDown={
                                    model.chipMenu ? (event) => onChipKeyDown(event, term.id, neighbour?.id) : undefined
                                  }
                                  onClick={(event) => {
                                    dismissHoverTooltip()
                                    const opening = !selected
                                    // Enter and Space reach a button as a click with no pointer detail; that
                                    // open is a keyboard one, so the menu has to take the focus off the chip.
                                    setChipMenuFocus(opening && event.detail === 0 ? { edge: 'first' } : null)
                                    setSelectedPillId(selected ? null : term.id)
                                  }}
                                  onFocus={(event) => {
                                    if (event.currentTarget.parentElement) {
                                      showChipTooltip(term, event.currentTarget.parentElement, 'focus')
                                    }
                                  }}
                                  onBlur={() =>
                                    setHoverTooltip((current) =>
                                      current?.code === term.id && current.source === 'focus' ? null : current
                                    )
                                  }
                                  className={`cursor-pointer text-left focus-visible:outline-none disabled:cursor-not-allowed${
                                    model.chipMenu ? ' before:absolute before:inset-0 before:rounded-full' : ''
                                  }`}
                                >
                                  {term.label}
                                </button>
                                {model.chipMenu ? null : (
                                  <button
                                    type="button"
                                    disabled={undoPending}
                                    aria-label={`Remove ${term.label}`}
                                    className="inline-flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-full text-sm leading-none opacity-60 hover:bg-white/70 dark:hover:bg-gray-700 hover:opacity-100 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-40"
                                    onClick={() => removeTerm(term.id)}
                                  >
                                    ×
                                  </button>
                                )}
                              </div>
                              {addedByClinician ? (
                                <span id={addedCueId} className="sr-only">
                                  Added by you
                                </span>
                              ) : null}
                              {orphan ? (
                                <span id={orphanCueId} className="sr-only">
                                  No longer in your summary
                                </span>
                              ) : null}
                              {selected && model.chipMenu ? (
                                <ClinicalSummaryChipMenu
                                  id={`${chipTriggerId(term.id)}-menu`}
                                  labelledBy={chipTriggerId(term.id)}
                                  focus={chipMenuFocus}
                                  moveTargets={chipMoveTargets(model, term.category)}
                                  disabled={undoPending}
                                  onMove={(category, fromKeyboard) => {
                                    // A move keeps the chip, so the handoff below normally claims the
                                    // focus; `fromKeyboard` still goes through so the toast is the
                                    // fallback stop whenever the chip cannot take it.
                                    changeCategory(term.id, category, { fromKeyboard, moved: true })
                                    if (fromKeyboard) chipToFocus.current = { code: term.id, preventScroll: false }
                                  }}
                                  onShowInSummary={
                                    quotedPhrase(term) ? (fromKeyboard) => showInSummary(term, fromKeyboard) : undefined
                                  }
                                  onRemove={(fromKeyboard) => {
                                    removeTerm(term.id, { fromKeyboard })
                                    if (fromKeyboard && neighbour) {
                                      chipToFocus.current = { code: neighbour.id, preventScroll: false }
                                    }
                                  }}
                                />
                              ) : selected ? (
                                // Floats so the chips beside it never move; the chip's mb-[30px] (4px gap + 26px control)
                                // holds its row open so the next section keeps its spacing.
                                <div className="absolute left-0 top-[calc(100%+4px)] z-20 flex">
                                  {model.options.map((option) => categoryButton(term.id, option, term.category))}
                                </div>
                              ) : null}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ) : null
                )}
              </div>
            ) : null}

            {removedTotal > 0 ? (
              <div className="flex flex-col gap-2" data-testid="clinical-summary-removed-group">
                <button
                  type="button"
                  onClick={() => setRemovedOpen((open) => !open)}
                  className="inline-flex w-fit items-center gap-1.5 pb-1 text-xs font-medium leading-5 text-text-body dark:text-gray-400"
                  aria-expanded={removedOpen}
                >
                  <CaretRight className={`size-3 shrink-0 ${removedOpen ? 'rotate-90' : ''}`} aria-hidden />
                  Removed {removedTotal}
                </button>
                {removedOpen ? (
                  <div className="flex flex-wrap gap-1.5">
                    {removedEntries.map((term) => (
                      <span
                        key={term.id}
                        className="inline-flex h-[30px] items-center gap-[5px] rounded-full border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 pl-[11px] pr-[3px]"
                      >
                        <span className="text-sm font-medium leading-5 text-text-subtle dark:text-gray-500 line-through">
                          {term.label}
                        </span>
                        <button
                          type="button"
                          disabled={undoPending}
                          aria-label={`Restore ${term.label}`}
                          onClick={(event) => restoreTerm(term.id, event.detail === 0)}
                          className="focus-visible:ring-brand-600 cursor-pointer rounded-full px-1 text-xs font-semibold leading-5 text-text-fg-brand dark:text-brand-400 hover:bg-background-brand-softer dark:hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Restore
                        </button>
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        )}
      </div>

      {model.collectsDifferential ? (
        // 15px plus the 1px border is the card's 16px inset: Figma draws the stroke inside the box.
        <div className="rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-[15px] shadow-xs">
          <Toggle
            size="card"
            label="Include Differential Diagnoses & Prior Genetic Testing"
            description="Optional. Provide any specific genetic conditions being considered for diagnosis. Phenotype terms will not be extracted from these fields."
            checked={differentialOpen}
            onChange={(open) => {
              setDifferentialOpen(open)
              // Off means the order carries no differential, so the saved text goes with it.
              if (!open && hasDifferentialText) {
                onSupplementalChange({ differentialDiagnosis: '', priorGeneticTesting: '' })
              }
            }}
            disabled={undoPending}
          />
        </div>
      ) : savedSupplemental.length > 0 ? (
        // This lab does not collect these fields; text an order already holds stays stored and visible.
        <dl
          data-testid="clinical-summary-saved-supplemental"
          className="grid gap-4 rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-4 md:grid-cols-2"
        >
          {savedSupplemental.map(({ label, value }) => (
            <div key={label} className="flex flex-col gap-1">
              <dt className="text-sm font-medium text-text-heading dark:text-white">{label}</dt>
              <dd className="whitespace-pre-wrap break-words text-sm text-text-body dark:text-gray-400">{value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {model.collectsDifferential && differentialOpen ? (
        <div className="grid gap-4 md:grid-cols-2">
          <TextareaInput
            label="Differential Diagnoses"
            value={differentialDiagnosis}
            rows={3}
            highlightInvalidChars
            helperText="Phenotype terms will not be extracted from the Differential Diagnosis."
            {...combinedCounterProps}
            onChange={(value) => {
              if (undoPending) return
              onSupplementalChange({ differentialDiagnosis: value, priorGeneticTesting })
            }}
          />
          <TextareaInput
            label="Prior Genetic Testing"
            value={priorGeneticTesting}
            rows={3}
            highlightInvalidChars
            helperText="Phenotype terms will not be extracted from Prior Genetic Testing."
            {...combinedCounterProps}
            onChange={(value) => {
              if (undoPending) return
              onSupplementalChange({ differentialDiagnosis, priorGeneticTesting: value })
            }}
          />
        </div>
      ) : null}
    </section>
  )
}
